import { createSelector } from "reselect"
import { get, chain, find, forEach } from "lodash"
import pluralize from "pluralize"
import { RESIDUAL_ITEM } from "helpers/residualAttribution"
import { getFactorsMetadata } from "selectors/factors"
import { getIsRfrAttributionEnabled } from "selectors/features"
import { getModelCategories } from "selectors/modelMetadata"

const EMPTY_ARRAY = []

const getRawSelectedMetric = state => get(state, "modules.comparePerformance.selectedMetric")
const getSavedMetric = (state, props) => get(props, "savedMetric")

const getData0 = state => get(state, "global.data.comparePerformancePositionSet0.data.model.simulation.performance")
const getData1 = state => get(state, "global.data.comparePerformancePositionSet1.data.model.simulation.performance")
const getData2 = state => get(state, "global.data.comparePerformancePositionSet2.data.model.simulation.performance")
const getData3 = state => get(state, "global.data.comparePerformancePositionSet3.data.model.simulation.performance")
const getData4 = state => get(state, "global.data.comparePerformancePositionSet4.data.model.simulation.performance")

const defaultMetric = "total"

function getCategory(category, factors) {
  if (category.singleFactor) {
    return {
      label: category.label,
      options: chain(factors)
        .pickBy(factor => factor.category === category.id)
        .map((factor, id) => ({
          label: factor.label,
          id,
          value: item => chain(item)
            .get("attribution.factors", EMPTY_ARRAY)
            .find(factor => factor.id === id)
            .get("value", 0)
            .value(),
        }))
        .value(),
    }
  } else {
    return {
      label: pluralize(category.label),
      options: [{
        label: `${category.label} Total`,
        id: `${category.id}_total`,
        value: item => chain(item)
          .get("attribution.factors", EMPTY_ARRAY)
          .filter(factor => factor.category === category.id)
          .sumBy("value")
          .value(),
      }, ...chain(factors)
        .pickBy(factor => factor.category === category.id)
        .map((factor, id) => ({
          label: factor.label,
          id,
          value: item => chain(item)
            .get("attribution.factors", EMPTY_ARRAY)
            .find(factor => factor.id === id)
            .get("value", 0)
            .value(),
        }))
        .value(),
      ],
    }
  }
}

export const getShowTradingPnl = createSelector(
  [getData0, getData1, getData2, getData3, getData4],
  (data0, data1, data2, data3, data4) => chain(data0)
    .concat(data1, data2, data3, data4)
    .some(elem => get(elem, "attribution.summary.trading.cumulativePercent", 0) !== 0)
    .value(),
)

export const getMetrics = createSelector(
  [getFactorsMetadata, getModelCategories, getShowTradingPnl, getIsRfrAttributionEnabled],
  (factors, categories, showTradingPnl, isRfrAttributionEnabled) => ([{
    label: "Cumulative Performance",
    options: [{
      label: "Total",
      id: "total",
      value: item => get(item, "total"),
    }, {
      label: "Factors",
      id: "factors",
      value: item => get(item, "attribution.summary.factors"),
    }, {
      label: "Alpha",
      id: "alpha",
      value: item => get(item, "attribution.summary.specific"),
    }, ...(showTradingPnl ? [{
      label: "Trading",
      id: "trading",
      value: item => get(item, "attribution.summary.trading"),
    }] : []), ...(isRfrAttributionEnabled ? [{
      label: RESIDUAL_ITEM.label,
      id: RESIDUAL_ITEM.id,
      value: item => get(item, "attribution.summary.residual"),
    }] : [])],
  }, ...chain(categories)
    .map(category => getCategory(category, factors))
    .value(),
  ]),
)

export const getSelectedMetric = createSelector(
  [getMetrics, getRawSelectedMetric, getSavedMetric],
  (metrics, selectedMetric, savedMetric) => {
    const metric = selectedMetric || savedMetric || defaultMetric
    let metricData = null
    let defaultMetricData = null

    forEach(metrics, item => {
      const data = find(item.options, { id: metric })

      if (data) {
        metricData = data
        return false
      }

      defaultMetricData = find(item.options, { id: defaultMetric }) || defaultMetricData
    })
    return metricData || defaultMetricData
  },
)
